/**
 * StudyHeader Component
 * Header section for the study detail page
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MoreVertical, ExternalLink, Brain, Edit, FileText, Loader2, RefreshCw, Check, Archive, ArchiveRestore } from 'lucide-react';
import type { Protocol } from '../types';
import { ENROLLMENT_STATUSES, type EnrollmentStatus } from '../types';
import { StatusBadge } from './StatusBadge';
import { StarButton } from './StarButton';
import api from '../../services/api';

interface StudyHeaderProps {
  protocol: Protocol;
  isStarred: boolean;
  onToggleStar: () => Promise<void>;
  onEdit?: () => void;
  onUpdateStatus?: (status: EnrollmentStatus) => Promise<void>;
  onArchive?: () => Promise<void>;
}

export function StudyHeader({ protocol, isStarred, onToggleStar, onEdit, onUpdateStatus, onArchive }: StudyHeaderProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isLoadingProtocol, setIsLoadingProtocol] = useState(false);
  const [protocolError, setProtocolError] = useState<string | null>(null);

  const handleViewProtocol = useCallback(async () => {
    if (isLoadingProtocol) return;

    setIsLoadingProtocol(true);
    setProtocolError(null);
    setShowMenu(false);

    try {
      let response = await api.get(
        `/api/intelligence/protocols/${protocol.id}/documents?document_type=protocol`
      );
      let docs = response.data;

      if (!docs || docs.length === 0) {
        response = await api.get(
          `/api/intelligence/protocols/${protocol.id}/documents`
        );
        docs = response.data;
      }

      if (!docs || docs.length === 0) {
        setProtocolError('No documents found for this protocol');
        setTimeout(() => setProtocolError(null), 4000);
        return;
      }

      const docId = docs[0].id;
      const downloadResponse = await api.get(
        `/api/intelligence/documents/${docId}/download`,
        { responseType: 'blob' }
      );

      const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (err: any) {
      console.error('Error viewing protocol document:', err);
      setProtocolError(err.response?.data?.error || 'Failed to load protocol document');
      setTimeout(() => setProtocolError(null), 4000);
    } finally {
      setIsLoadingProtocol(false);
    }
  }, [protocol.id, isLoadingProtocol]);

  const handleUpdateStatus = useCallback(async (status: EnrollmentStatus) => {
    if (!onUpdateStatus || isUpdatingStatus || status === protocol.enrollment_status) return;

    setIsUpdatingStatus(true);
    setShowStatusMenu(false);
    setProtocolError(null);

    try {
      await onUpdateStatus(status);
    } catch (err: any) {
      console.error('Error updating enrollment status:', err);
      setProtocolError(err.response?.data?.error || 'Failed to update enrollment status');
      setTimeout(() => setProtocolError(null), 4000);
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [onUpdateStatus, isUpdatingStatus, protocol.enrollment_status]);

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Back link */}
        <div className="mb-4">
          <Link
            to="/siteorganizer"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeft size={16} />
            Back to Studies
          </Link>
        </div>

        {/* Header content */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            {/* Sponsor name */}
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {protocol.sponsor_name || 'Unknown Sponsor'}
            </p>

            {/* Protocol number & indication */}
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
                {protocol.protocol_number}
              </h1>
              {protocol.indication && (
                <>
                  <span className="text-gray-300 dark:text-gray-600 text-lg">|</span>
                  <span className="text-lg text-gray-500 dark:text-gray-400">{protocol.indication}</span>
                </>
              )}
            </div>

            {/* Status & site */}
            <div className="flex items-center gap-3 mt-2">
              <div className="relative">
                {isUpdatingStatus ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    <Loader2 size={14} className="animate-spin" />
                    Updating...
                  </span>
                ) : (
                  <StatusBadge status={protocol.enrollment_status} />
                )}

                {showStatusMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowStatusMenu(false)}
                    />
                    <div className="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1 dark:bg-gray-800 dark:border-gray-700">
                      {ENROLLMENT_STATUSES.map((s) => {
                        const isCurrent = s.value === protocol.enrollment_status;
                        return (
                          <button
                            key={s.value}
                            onClick={() => handleUpdateStatus(s.value)}
                            disabled={isCurrent}
                            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                              isCurrent
                                ? 'text-gray-400 bg-gray-50 cursor-default dark:text-gray-500 dark:bg-gray-700/50'
                                : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                              s.color === 'yellow' ? 'bg-yellow-400' :
                              s.color === 'green' ? 'bg-green-500' :
                              s.color === 'gray' ? 'bg-gray-400' :
                              s.color === 'blue' ? 'bg-blue-500' :
                              'bg-red-500'
                            }`} />
                            <span className="flex-1">{s.label}</span>
                            {isCurrent && <Check size={14} className="text-gray-400 dark:text-gray-500" />}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-300">{protocol.site_name}</span>
              {protocol.study_phase && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <span className="text-sm text-gray-600 dark:text-gray-300">{protocol.study_phase}</span>
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <StarButton isStarred={isStarred} onToggle={onToggleStar} size="lg" />

            {/* Action dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <MoreVertical size={20} className="text-gray-500 dark:text-gray-400" />
              </button>

              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />
                  <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1 dark:bg-gray-800 dark:border-gray-700">
                    {onEdit && (
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onEdit();
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Edit size={16} />
                        Edit Study Details
                      </button>
                    )}
                    {onUpdateStatus && (
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setShowStatusMenu(true);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <RefreshCw size={16} />
                        Update Enrollment Status
                      </button>
                    )}
                    <button
                      onClick={handleViewProtocol}
                      disabled={isLoadingProtocol}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                    >
                      {isLoadingProtocol ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <FileText size={16} />
                      )}
                      View Protocol Document
                    </button>
                    <a
                      href={`/intelligence/chat?protocol_id=${protocol.id}`}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Brain size={16} />
                      Ask AI about this study
                    </a>
                    <a
                      href={`https://clinicaltrials.gov/search?term=${encodeURIComponent(protocol.protocol_number)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <ExternalLink size={16} />
                      View on ClinicalTrials.gov
                    </a>
                    {onArchive && (
                      <>
                        <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            if (protocol.status === 'inactive') {
                              onArchive();
                            } else {
                              if (window.confirm('Are you sure you want to archive this study? It will be hidden from the default study list.')) {
                                onArchive();
                              }
                            }
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          {protocol.status === 'inactive' ? (
                            <>
                              <ArchiveRestore size={16} />
                              Unarchive Study
                            </>
                          ) : (
                            <>
                              <Archive size={16} />
                              Archive Study
                            </>
                          )}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        onToggleStar();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      {isStarred ? 'Unstar from My Studies' : 'Star as My Study'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {protocolError && (
          <div className="mt-2 text-sm text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20 px-3 py-1.5 rounded-lg">
            {protocolError}
          </div>
        )}
      </div>
    </div>
  );
}

export default StudyHeader;
